import React, { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";

import { Callout } from "@/components/ui/Callout";
import Skeleton from "@/components/shared/Skeleton";
import { PlatformService } from "@/services/platformService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { plural } from "@/utils/formatText";

import { DETAIL_LIST, TONE } from "../constants";
import Chip from "./Chip";
import DetailDrawer from "./DetailDrawer";
import ErrorNote from "./ErrorNote";

// The three kinds of change, in the order the sync applies them: removes first
// (so a cascade cleans up), then updates, then inserts. Reading them in that
// order is reading what the sync will do.
const GROUPS = [
    {
        key: "removed",
        title: "To remove from this database",
        tone: TONE.rose,
        hint:
            "Present here and not in the platform catalog. Removing one also " +
            "removes it from every role in this database that held it.",
    },
    {
        key: "updated",
        title: "To update",
        tone: TONE.amber,
        hint: "Same code on both sides, with one or more fields out of step.",
    },
    {
        key: "added",
        title: "To add",
        tone: TONE.emerald,
        hint: "In the platform catalog and missing here.",
    },
];

const Row = ({ row }) => (
    <div className="px-3 py-2">
        <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[12px] text-text-primary">{row.code}</span>
            {row.category && (
                <span className="text-[11px] text-text-secondary">{row.category}</span>
            )}
            {!row.isActive && (
                <span className="text-[11px] text-tone-slate">inactive</span>
            )}
        </div>
        {row.displayName && (
            <div className="text-[11px] text-text-secondary mt-0.5">
                {row.displayName}
            </div>
        )}
        {row.changes?.length > 0 && (
            <div className="mt-1 space-y-0.5">
                {row.changes.map((change) => (
                    <div
                        key={change.field}
                        className="text-[11px] text-text-secondary font-mono break-words"
                    >
                        <span className="text-text-muted">{change.field}: </span>
                        {change.fromValue ?? "—"}{" "}
                        <span className="text-text-muted">→</span>{" "}
                        <span className="text-text-primary">
                            {change.toValue ?? "—"}
                        </span>
                    </div>
                ))}
            </div>
        )}
    </div>
);

/**
 * Which permission codes are drifted on one database.
 *
 * The column can only hold `+3 ~1 -0`. This names the codes behind those
 * numbers, and for an update says which of the four synced fields differ and
 * what they are moving from and to — the part that decides whether a sync is
 * safe to run now or wants a look first.
 *
 * Fetched on open rather than with the fleet snapshot: the status call already
 * opens a connection per dedicated database, and this detail is only ever wanted
 * for one row at a time.
 */
const PermissionsDetailDrawer = ({ row, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!row?.tenantId) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await PlatformService.fetchPermissionsDetail(row.tenantId);
                if (!cancelled) setDetail(data);
            } catch (err) {
                if (!cancelled) setError(apiErrorMessageFormat(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [row?.tenantId]);

    return (
        <DetailDrawer
            open={!!row}
            onClose={onClose}
            title="Permission catalog"
            subtitle={row?.label}
        >
            {loading && (
                <div className="space-y-2">
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-24 w-full" />
                </div>
            )}

            {!loading && error && <ErrorNote>{error}</ErrorNote>}

            {!loading && !error && detail && (
                <>
                    <div className="flex items-center gap-2 flex-wrap text-[11px] text-text-secondary">
                        <span>
                            {detail.dedicatedTotal} codes here ·{" "}
                            {detail.platformTotal} in the platform catalog
                        </span>
                    </div>

                    {detail.inSync ? (
                        <Callout tone="neutral" icon={CheckCircle2} size="xs">
                            This database carries the platform catalog exactly. Nothing
                            to sync.
                        </Callout>
                    ) : (
                        GROUPS.map((group) => {
                            const rows = detail[group.key] ?? [];
                            if (!rows.length) return null;
                            return (
                                <div key={group.key}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="text-xs font-medium text-text-secondary">
                                            {group.title}
                                        </div>
                                        <Chip
                                            tone={group.tone}
                                            label={plural(rows.length, "code", "codes")}
                                            title={group.hint}
                                        />
                                    </div>
                                    <div className={DETAIL_LIST}>
                                        {rows.map((permission) => (
                                            <Row key={permission.code} row={permission} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </>
            )}
        </DetailDrawer>
    );
};

export default PermissionsDetailDrawer;
